import { useTranslation } from 'next-i18next';
import React, { useEffect, useMemo } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { NftTabsEmptyState } from 'src/features/common/collectible/components/nft-tabs-empty-state';
import { StyledWrapper } from 'src/features/common/collectible/containers/nft-lots-container/styles';
import { useActiveLots, useAllLots, useCollectible } from 'src/features/common/collectible/hooks/collectible.hooks';
import { useActiveBuy } from 'src/features/common/collectible/hooks/use-active-buy';
import { useActiveOwnersByIds } from 'src/features/common/collectible/hooks/use-nft-owners';
import { INftLotsLocale } from 'src/features/common/collectible/types/locales';
import { useFetchUserList } from 'src/features/common/user/hooks/user-list.hooks';
import { useMyWalletsById } from 'src/features/common/wallet/hooks/use-my-wallets-by-id';
import { AnimatedArrow } from 'src/ui/common/atoms/animated-arrow';
import { Typography } from 'src/ui/common/atoms/typography';
import { Table } from 'src/ui/common/molecules/table';
import { NftLotRow } from '../../components/nft-lot-row';

export const NftLotsContainer = () => {
    const { t } = useTranslation(['actions', 'pages']);
    const allLots = useAllLots();
    const myWallets = useMyWalletsById();
    const activeOwnersByIds = useActiveOwnersByIds();
    const fetchUserList = useFetchUserList();
    const activeLots = useActiveLots();
    const nft = useCollectible();
    const { isActiveLotsHasMyNft } = useActiveBuy();

    const lotsToShow = useMemo(
        () => allLots.filter((lot) => !lot.isDeleted && lot.isPublished && activeOwnersByIds[lot.owner]),
        [allLots, myWallets, activeOwnersByIds]
    );

    const locale = useMemo(
        () => ({
            ...(t('nft.view.lots', { returnObjects: true, ns: 'pages' }) as INftLotsLocale),
            loadMore: t('misc.loadMore', { ns: 'actions' }),
        }),
        [t]
    );

    useEffect(() => {
        fetchUserList({
            wallets: lotsToShow.map((lot) => ({
                blockchainId: nft?.blockchainId!,
                address: lot.author,
            })),
        });
    }, [fetchUserList, lotsToShow, nft?.blockchainId]);

    if (!lotsToShow.length) {
        return (
            <NftTabsEmptyState title={t('nft.view.lots.emptyTitle', { ns: 'pages' })}>
                {t('nft.view.lots.emptyText', { ns: 'pages' })}
            </NftTabsEmptyState>
        );
    }

    return (
        <StyledWrapper>
            <Table stretch>
                <Table.Head>
                    <Table.HeadCell>
                        <Typography variant="title" size="sm">
                            {locale.owner}
                        </Typography>
                    </Table.HeadCell>
                    <Table.HeadCell>
                        <Typography variant="title" size="sm">
                            {locale.status}
                        </Typography>
                    </Table.HeadCell>
                    <Table.HeadCell>
                        <Typography variant="title" size="sm">
                            {locale.timeLeft}
                        </Typography>
                    </Table.HeadCell>
                    <Table.HeadCell>
                        <Typography variant="title" size="sm">
                            {locale.editions}
                        </Typography>
                    </Table.HeadCell>
                    <Table.HeadCell>
                        <Typography variant="title" size="sm">
                            {locale.price}
                        </Typography>
                    </Table.HeadCell>
                    <Table.HeadCell>
                        <Typography variant="title" size="sm">
                            {locale.action}
                        </Typography>
                    </Table.HeadCell>
                </Table.Head>
                <Table.Body>
                    {lotsToShow?.map((lot) => (
                        <NftLotRow nft={nft!} lot={lot} key={lot.id} />
                    ))}
                    {isMobileOnly && activeLots.length > 0 && (
                        <AnimatedArrow bottomPosition={-20} label={!isActiveLotsHasMyNft ? 'Slide to Buy Now' : ''} />
                    )}
                </Table.Body>
            </Table>
        </StyledWrapper>
    );
};